/* ------------------------------------------------------------------ */
/*  Upsell Adapter — turns the /api/get-all-products payload into what  */
/*  the upsell UI needs. NO hardcoded product data. Only structural    */
/*  maps (internal_id -> which dedicated step a product belongs to).    */
/*                                                                      */
/*  Mirrors forms-newself .../glp1-deal-1/upsell/products.ts so the Vue */
/*  upsell behaves identically to the Next.js reference app.            */
/* ------------------------------------------------------------------ */

import { isFastTrackProduct } from '@/utils/fastTrackAddon';
import { nadProductIds } from '@/utils/freeNadLifeOffer';

/* Structural maps — map internal_ids to UI behavior, not product data. */
export const INTERNAL_IDS = {
    GLP_BUNDLE: 'GLP_BUNDLE',
    METFORMIN: 'METFORMIN',
    DHEA: 'DHEA',
    NALTREXONE: 'NALTREXONE',
    GLP_BALANCE: 'GLP_BALANCE',
    ONDANSETRON: 'ONDANSETRON',
    SERMORELIN: 'SERMORELIN',
    NAD: 'NAD',
    NAD_NASAL: 'NAD_NASAL'
};

/* internal_ids that have their own dedicated step (bundle / NAD / Sermorelin). */
export const DEDICATED_STEP_INTERNAL_IDS = new Set([
    INTERNAL_IDS.GLP_BUNDLE,
    INTERNAL_IDS.NAD,
    INTERNAL_IDS.NAD_NASAL,
    INTERNAL_IDS.SERMORELIN
]);

/* internal_ids kept off the upsell even while the DB marks them add-ons. They stay
   is_addon in the DB because the backend team flagged that clearing it can break
   orders that still sell them (the call-center cart-link offer). NAD_NASAL_3000 is
   the free-nad-nasal-life bottle; the NAD step sells the paid NAD+ Nasal. */
export const UPSELL_BLOCKED_INTERNAL_IDS = new Set(['NAD_NASAL_3000', 'FOREVER_SPARK']);

/* Plan lengths the upsell sells: 0 one-time, 1 monthly, 3 three months. The bundle
   and the individual cards offer only monthly and three months. */
export const UPSELL_TERMS = [0, 1, 3];
export const CARD_TERMS = [1, 3];

/* ------------------------------------------------------------------ */
/*  Product lookups                                                    */
/* ------------------------------------------------------------------ */

export function getActiveAddons(products) {
    if (!Array.isArray(products)) return [];
    // Priority Physician Review is sold only inside checkout, never on the upsell.
    // Excluded by internal_id rather than is_addon because that flag may change.
    // A product with no priced plan would print $0.00, so it stays off.
    return products.filter(
        (p) => p && p.is_active === true && p.is_addon === true && !isFastTrackProduct(p)
            && !UPSELL_BLOCKED_INTERNAL_IDS.has(p.internal_id)
            && pricedTerms(p, UPSELL_TERMS).length > 0
    );
}

export function getProductByInternalId(products, internalId) {
    if (!Array.isArray(products)) return undefined;
    return products.find((p) => p && p.internal_id === internalId);
}

export function getProductById(products, id) {
    if (!Array.isArray(products)) return undefined;
    return products.find((p) => p && (p.id === id || p._id === id));
}

export function getBundleProduct(products) {
    return getProductByInternalId(products, INTERNAL_IDS.GLP_BUNDLE);
}

export function getBundleComponents(products, bundleProduct) {
    if (!bundleProduct || !Array.isArray(bundleProduct.bundle_components) || !bundleProduct.bundle_components.length) {
        return [];
    }
    return bundleProduct.bundle_components
        .map((internalId) => getProductByInternalId(products, internalId))
        .filter(Boolean);
}

/* Individual-step products: active addons not routed to a dedicated step, with a
   priced plan the card's Monthly / 3-Month picker can sell. */
export function getIndividualStepProducts(products) {
    return getActiveAddons(products).filter(
        (p) => (!p.internal_id || !DEDICATED_STEP_INTERNAL_IDS.has(p.internal_id))
            && pricedTerms(p, CARD_TERMS).length > 0
    );
}

/* ------------------------------------------------------------------ */
/*  NAD+ already held                                                  */
/*  One NAD+ per order: the NAD step hides while the shopper holds one. */
/* ------------------------------------------------------------------ */

const ENDED_STATUSES = new Set(['canceled', 'cancelled', 'ended']);

/* True when a subscription from GET /api/subscription-items is a NAD+ product, or a
   plan that ships one (the Total Reset plans), and is not canceled or ended. With no
   list it cannot tell, so it answers true and the upsell sells no second NAD+. */
export function holdsNad(subscriptions, products) {
    if (!Array.isArray(subscriptions)) return true;
    const nadIds = nadProductIds(products);
    return subscriptions.some((s) => s && nadIds.has(s.product_id) && !ENDED_STATUSES.has(s.status));
}

/* ------------------------------------------------------------------ */
/*  Gender restriction                                                 */
/*  Backend codes: "F" = female-only, "M" = male-only, null = anyone.   */
/*  user.gender arrives as the full word ("Male"/"Female").             */
/* ------------------------------------------------------------------ */

export function passesGenderRestriction(product, user) {
    const restriction = product && product.gender_restriction;
    if (!restriction) return true;
    if (!user || !user.gender) return false;
    return user.gender.charAt(0).toUpperCase() === restriction.charAt(0).toUpperCase();
}

export function filterByGender(products, user) {
    if (!Array.isArray(products)) return [];
    return products.filter((p) => passesGenderRestriction(p, user));
}

/* ------------------------------------------------------------------ */
/*  Plans (pricing) — driven by product.pricing_plans[]                */
/* ------------------------------------------------------------------ */

function apiPlanToUpsellPlan(plan, product) {
    return {
        plan_id: plan.id,
        name: plan.name != null ? plan.name : product.name,
        product_id: product.id,
        terms: plan.terms,
        price: plan.price
    };
}

/* A plan the upsell can sell: active, with a price above $0. The upsell never sells
   anything for $0. */
const isSellablePlan = (p) => p && p.is_active !== false && Number(p.price) > 0;

export function getPlansForProduct(product) {
    if (!product || !Array.isArray(product.pricing_plans)) return [];
    return product.pricing_plans
        .filter(isSellablePlan)
        .map((p) => apiPlanToUpsellPlan(p, product));
}

export function getPlan(product, terms) {
    if (!product || !Array.isArray(product.pricing_plans)) return undefined;
    const match = product.pricing_plans.find((p) => isSellablePlan(p) && p.terms === terms);
    return match ? apiPlanToUpsellPlan(match, product) : undefined;
}

/* The plan lengths, out of `terms`, this product has a sellable plan for. */
export function pricedTerms(product, terms) {
    return terms.filter((t) => getPlan(product, t));
}

/* `wanted` when the product sells it, else its first sellable length from `terms`.
   With none it keeps `wanted`: such a product is already off the upsell. */
export function pickTerms(product, wanted, terms) {
    const priced = pricedTerms(product, terms);
    return priced.includes(wanted) || !priced.length ? wanted : priced[0];
}

/* ------------------------------------------------------------------ */
/*  Display helpers                                                    */
/* ------------------------------------------------------------------ */

export function formatPrice(price) {
    const str = Number(price || 0).toFixed(2);
    const parts = str.split('.');
    return { dollars: Number(parts[0]).toLocaleString('en-US'), cents: parts[1] };
}

export function inflatedPrice(price) {
    return Math.ceil(Number(price || 0) * 1.25);
}

/* The DB sometimes stores product images on the wrong bucket/host. Force any product
   image URL onto the NewSelf bucket: if it's already on the NewSelf base, leave it
   alone; otherwise swap whatever host/prefix sits before the shared product-image path
   for the NewSelf base, keeping the `<slug>/<file>` tail. URLs that don't contain the
   product-image path (logos, icons, etc.) pass through untouched — there's nothing to
   map. No env dependency, so it works regardless of build config. */
const RIGHT_IMAGE_BASE = 'https://newselfrx.s3.us-east-1.amazonaws.com/public/assets/product_image/webp/';
const PRODUCT_IMAGE_MARKER = 'public/assets/product_image/webp/';

export function normalizeImageUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf(RIGHT_IMAGE_BASE) === 0) return url;
    const i = url.indexOf(PRODUCT_IMAGE_MARKER);
    if (i === -1) return url;
    return RIGHT_IMAGE_BASE + url.slice(i + PRODUCT_IMAGE_MARKER.length);
}

export function getProductImage(product) {
    const url = (product && product.images && product.images[0] && product.images[0].url) || '';
    return normalizeImageUrl(url);
}

export function getProductImageById(products, id) {
    return getProductImage(getProductById(products, id));
}
